import { Fade, Tab, Tabs, useTheme } from '@mui/material';
import { isCreditPurchaseProduct } from '@vy/services';
import type { FixedProductInput, UsageProductInput } from '@vy/types';
import { CONTRACT_TYPES, DEFAULT_CURRENCY } from '@vy/types';
import { isNotNullOrUndefined, validateEnum, withDefault } from '@vy/utils';
import { get } from 'lodash';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { withSuspenseBoundary } from '../../../../../components/utility/SuspenseBoundary';
import { useCreatePlanContext } from '../../../../../contexts/create-plan-context';
import { useFormErrorNotificationContext } from '../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useNetSuiteProducts } from '../../../../../hooks/requests/integrations';
import { useGetContractPrerequisites } from '../../create-plan-page-hooks/useGetContractPrerequisites';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { TabPanel } from './create-plan-page-tabs-components/TabPanel';
import type { CreatePlanTab } from './create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from './create-plan-page-types/create-plan-tab';
import { getCreatePlanTabs } from './create-plan-tabs-consts/create-plan-tabs';

const CreatePlanPageTabs: React.FC = () => {
  const {
    getValues,
    formState: { errors },
    watch,
  } = useFormContext<PlanInputForm>();
  const { customerId, contractType } = getValues();
  const { createPlanTab: currentTab, setCreatePlanTab: setCurrentTab } = useCreatePlanContext();
  const { errorsFields } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const contractPrerequisites = useGetContractPrerequisites();
  useNetSuiteProducts(customerId);

  const isParentAgreement = [contractPrerequisites?.contractType, contractType].includes(
    CONTRACT_TYPES.ParentAgreement,
  );

  const theme = useTheme();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    validateEnum(newValue, CREATE_PLAN_TABS);
    setCurrentTab(newValue);
  };

  const allFixedProducts = withDefault(watch('fixedProducts') as FixedProductInput[], []);
  const serviceFees = allFixedProducts.filter((product) => !isCreditPurchaseProduct(product));

  const createPlanTabs = getCreatePlanTabs({
    fixedProducts: [...serviceFees],
    usageProducts: [...withDefault(watch('usageProducts') as UsageProductInput[], [])],
    promotionProducts: [...withDefault(watch('promotionProducts') as FixedProductInput[], [])],
    currency: DEFAULT_CURRENCY,
    doesHaveError: (tab) =>
      errorsFields.some(
        (errorField) => errorField.tab === tab && isNotNullOrUndefined(get(errors, errorField.fieldPath)),
      ),
    isParentAgreement,
  });

  return (
    <div className="flex flex-col h-full w-full">
      <div className="pt-4 bg-white">
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          style={{
            padding: '0 24px',
            backgroundColor: 'white',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {Object.entries(createPlanTabs).map(([createPlanTab, { id, label, selectorAttribute }]) => (
            <Tab key={id} label={label} value={createPlanTab} {...selectorAttribute} />
          ))}
        </Tabs>
      </div>

      <div className="px-10 h-full flex flex-col w-full overflow-y-auto mb-10">
        {Object.entries(createPlanTabs).map(([createPlanTab, { id, render }]) => (
          <TabPanel key={id} hidden={currentTab !== createPlanTab}>
            <Fade in={currentTab === createPlanTab}>
              <div className="h-full w-full">{render}</div>
            </Fade>
          </TabPanel>
        ))}
      </div>
    </div>
  );
};

const CreatePlanPageTabsWithSuspense = withSuspenseBoundary(CreatePlanPageTabs);
export { CreatePlanPageTabsWithSuspense as CreatePlanPageTabs };
